<script setup lang="ts">
  import { onMounted, onUnmounted } from 'vue';
  import { ColoredButton } from '@/shared/ui';
  import { useFilterAndSort } from '@/shared/lib';
  import orderIcon from '@/shared/assets/icons/Order.svg';
  import orderWhite from '@/shared/assets/icons/OrderWhite.svg';
  import CheckMarkIcon from '@/shared/assets/icons/CheckMarkIcon.svg';

  const props = defineProps<{
    hasChanges: boolean;
    isSaving: boolean;
    orderOptions?: Array<{ label: string; sort: string; order: string }>;
  }>();

  const emit = defineEmits<{
    (e: 'save'): void;
    (e: 'update:sort', sort: string, order: string): void;
    (e: 'filter-clicked'): void;
  }>();

  const {
    isOrderSelected,
    isOrderDropdownOpen,
    selectedOption,
    dropdownPosition,
    orderButtonRef,
    handleOrder,
    sortOptions,
    handleClickOutside,
  } = useFilterAndSort(emit);

  const handleSortOptions = (sort: string, order: string) => {
    sortOptions(sort, order);
  };

  const handleSave = () => {
    emit('save');
  };

  onMounted(() => {
    document.addEventListener('click', handleClickOutside);
  });

  onUnmounted(() => {
    document.removeEventListener('click', handleClickOutside);
  });
</script>

<template>
  <div class="flex flex-shrink-0 items-center gap-4 relative">
    <div
      v-if="orderOptions && orderOptions.length > 0"
      ref="orderButtonRef"
      class="relative"
    >
      <ColoredButton
        v-if="isOrderSelected"
        text="Ordenar por"
        :icon="orderWhite"
        icon-alt="Ícone de ordenação"
        :outlined="false"
        class="!h-[42px] border border-[var(--theme-secondary-ink)]"
        @click="handleOrder"
      />
      <ColoredButton
        v-else
        text="Ordenar por"
        :icon="orderIcon"
        icon-alt="Ícone de ordenação"
        :outlined="true"
        class="!h-[42px]"
        @click="handleOrder"
      />

      <Teleport to="body">
        <transition
          enter-active-class="transition duration-200 ease-out"
          enter-from-class="transform opacity-0"
          enter-to-class="opacity-100 translate-y-0"
          leave-active-class="transition duration-200 ease-in"
          leave-from-class="opacity-100"
          leave-to-class="transform opacity-0"
        >
          <div
            v-if="isOrderDropdownOpen"
            id="order-dropdown"
            class="fixed w-72 p-4 bg-white-01 rounded-2xl shadow-2xl z-50"
            :style="{ top: dropdownPosition.top + 'px', right: dropdownPosition.right + 'px' }"
          >
            <div class="py-1">
              <button
                v-for="option in orderOptions"
                :key="option.label"
                class="block w-full text-left px-4 py-2 text-md text-gray-07 font-medium"
                :class="{
                  'bg-gray-02 rounded-lg':
                    selectedOption?.sort === option.sort && selectedOption?.order === option.order,
                }"
                @click="handleSortOptions(option.sort, option.order)"
              >
                {{ option.label }}
              </button>
            </div>
          </div>
        </transition>
      </Teleport>
    </div>

    <ColoredButton
      text="Salvar"
      :icon="CheckMarkIcon"
      icon-alt="Ícone de salvar"
      :disabled="!hasChanges || isSaving"
      class="!h-[42px]"
      @click="handleSave"
    />
  </div>
</template>
